import React from 'react'
import { WeekSummary } from '../../../../../backend/src/services/resources/missions/missions.summary.service.types'
import Table, { ExtendedColumn } from '../../table'
import { allDaysOfInterval, getWeekdayMondayBased, type WeekDay } from '../../../utils/date'
import useStore from '../../../store'
import { useValidateMutation } from '../../../queries/work-periods'
import { WorkPeriodWithMission } from '../../../api/work-periods'
import { TableRowData } from './types'
import { addColumnGroup } from './utils'
import workerColumnGroup from './column-groups/worker-column-group'
import workerCountersColumnGroup from './column-groups/worker-counters-column-group'
import weekStateColumnGroup from './column-groups/week-state-column-group'
import missionDetailsColumnGroup from './column-groups/mission-details-column-group'
import totalsDurationsColumnsGroup from './column-groups/totals-durations-columns-group'
import costsColumnGroup from './column-groups/costs-columns-group'
import createWorkPeriodColumnsGroup from './column-groups/work-period-columns-group'
import useTableData from './hooks/use-table-data'
import { FiltersResourcesEnum } from '../../../store/filters'
import weekActionsColumnsGroup from './column-groups/week-actions-columns-group'
import { Organization, OrganizationTypeEnum } from '../../../api/organizations'
import { compareDocsById } from '../../../utils/documents'
import { SpecialPeriod } from '../../../api/special-periods'
import { getDay, isWithinInterval } from 'date-fns'

export interface SummaryTableProps {
  data: WeekSummary
  displayDay?: Date
  start: Date
  end: Date
  hideActions?: boolean
  minimalMode?: boolean
  forceEmployerRules?: boolean // TODO: refactor => acces to filters is not good
}

const SummaryTableV2: React.FC<SummaryTableProps> = ({
  data,
  displayDay,
  start,
  end,
  hideActions,
  minimalMode,
  forceEmployerRules,
}) => {
  const [excludeGroupKeys, setExcludeGroupKeys] = React.useState<string[]>([])
  const currentOrganization = useStore(s => s.session.currentOrganization) as Organization
  const summaryFilters = useStore(s => s.filters[FiltersResourcesEnum.missions]) // TODO: refactor => acces to filters is not good

  const daysWithoutWorkPeriods: number[] = React.useMemo(() => {
    if (!minimalMode) return []
    // On crée un tableau avec tous les jours de la semaine (0-6)
    const allWeekDays = [0, 1, 2, 3, 4, 5, 6]

    // On filtre pour ne garder que les jours qui n'ont pas de périodes de travail
    return allWeekDays.filter(weekDay => {
      // On crée une date pour ce jour de la semaine
      const dayDate = new Date(start)
      dayDate.setDate(dayDate.getDate() + weekDay)

      // On vérifie si le jour a des périodes de travail
      const hasWorkPeriods = data.users.some(user =>
        user.missions.some(mission =>
          mission.workPeriods.some(
            workPeriod => new Date(workPeriod.start.date).toDateString() === dayDate.toDateString(),
          ),
        ),
      )

      // On retourne true si le jour n'a pas de périodes de travail
      return !hasWorkPeriods
    })
  }, [data, start])

  // Détermine les jours à afficher
  const days = React.useMemo(() => {
    if (displayDay) {
      return [new Date(displayDay)]
    }
    return allDaysOfInterval(new Date(start), new Date(end))
  }, [displayDay, start, end])

  let employerRules = data.employerRules

  if (
    currentOrganization.type === OrganizationTypeEnum.interimAgency &&
    summaryFilters.employer.length !== 1 &&
    !forceEmployerRules
  ) {
    employerRules = []
  }

  let specialPeriods = data.specialPeriods
  if (
    currentOrganization.type === OrganizationTypeEnum.interimAgency &&
    summaryFilters.employer.length !== 1 &&
    !forceEmployerRules
  ) {
    specialPeriods = data.specialPeriods.filter(specialPeriod => {
      return compareDocsById(specialPeriod.employer, currentOrganization._id)
    })
  }

  const tableColumns = useColumns(
    start,
    end,
    employerRules,
    setExcludeGroupKeys,
    excludeGroupKeys,
    days,
    daysWithoutWorkPeriods,
    specialPeriods as SpecialPeriod[],
    data.counterTemplates ?? [],
    displayDay,
    hideActions,
    minimalMode,
  )

  const tableData = useTableData(data.users, excludeGroupKeys)

  // Handle sticky first sections (worker & week state)
  const tableRef = React.useRef<HTMLTableElement>(null)
  const summaryPagination = useStore(s => s.filters.pagination.summary)

  React.useEffect(() => {
    if (!tableRef.current) return

    const updateStickyColumns = () => {
      const rows = tableRef.current?.querySelectorAll('tr') as NodeListOf<HTMLTableRowElement>
      if (!rows) return

      rows.forEach(row => {
        let offset = 0
        const cols = row.querySelectorAll('.sticky') as NodeListOf<HTMLTableCellElement>
        cols.forEach(col => {
          col.style.left = `${offset}px`
          offset += col.offsetWidth
        })
      })
    }

    // Initial update
    updateStickyColumns()

    // Add resize listener
    window.addEventListener('resize', updateStickyColumns)

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateStickyColumns)
    }
  }, [tableRef, summaryFilters, summaryPagination, excludeGroupKeys])

  return (
    <Table
      columns={tableColumns}
      data={tableData}
      noDataMessage="Aucune mission à afficher"
      className="ml-2"
      tableRef={tableRef}
      dataTest="summary-table"
    />
  )
}

const useColumns = (
  start: Date,
  end: Date,
  employerRules: WeekSummary['employerRules'],
  setExcludeGroupKeys: (excludeGroupKeys: string[]) => void,
  excludeGroupKeys: string[],
  days: Date[],
  daysWithoutWorkPeriods: number[],
  specialPeriods: SpecialPeriod[],
  counterTemplates: WeekSummary['counterTemplates'],
  displayDay?: Date,
  hideActions?: boolean,
  minimalMode?: boolean,
): ExtendedColumn<TableRowData>[] => {
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const workPeriodValidateMutation = useValidateMutation()

  const handleValidation = (workPeriod: WorkPeriodWithMission) => {
    workPeriodValidateMutation.mutate({
      workPeriodId: workPeriod._id,
      isValidated: !workPeriod.isValidated,
    })
  }

  const daysEmployerRules = employerRules.filter(rule => rule.display.summary.days)

  return React.useMemo(() => {
    const summaryColumns: ExtendedColumn<TableRowData>[] = []

    //User name section
    addColumnGroup(summaryColumns, workerColumnGroup())

    //Week state section
    addColumnGroup(
      summaryColumns,
      weekStateColumnGroup({
        start,
        minimalMode,
      }),
    )

    // Mission section
    addColumnGroup(
      summaryColumns,
      missionDetailsColumnGroup({
        currentUserRole,
        minimalMode,
        setExcludeGroupKeys,
        excludeGroupKeys,
      }),
    )
    // Days section
    days.forEach((day: Date, dayIndex: number) => {
      const isDayWithoutWorkPeriods = days.length !== 1 && daysWithoutWorkPeriods.includes(dayIndex)
      /** Monday=0..Sunday=6; must not use forEach index (always 0 when only one column is shown). */
      const weekDayMondayBased = getWeekdayMondayBased(getDay(day) as WeekDay)
      const employerRulesForDay = daysEmployerRules.filter(rule =>
        rule.daysOfWeek?.includes(weekDayMondayBased),
      )

      const specialPeriodsForDay = specialPeriods.filter(specialPeriod => {
        return isWithinInterval(day, {
          start: new Date(specialPeriod.startDate),
          end: new Date(specialPeriod.endDate),
        })
      })
      addColumnGroup(
        summaryColumns,
        createWorkPeriodColumnsGroup({
          day,
          dayIndex,
          handleValidation,
          hideActions,
          minimalMode,
          employerRules: employerRulesForDay,
          isDayWithoutWorkPeriods,
          specialPeriods: specialPeriodsForDay,
        }),
      )
    })

    if (!displayDay) {
      // Total durations section
      addColumnGroup(
        summaryColumns,
        totalsDurationsColumnsGroup({
          hideActions,
          minimalMode,
          employerRules,
        }),
      )
      // Costs section
      addColumnGroup(
        summaryColumns,
        costsColumnGroup({
          hideActions,
          minimalMode,
          employerRules,
        }),
      )

      if (counterTemplates.length > 0) {
        const countersGroup = workerCountersColumnGroup({
          counterTemplates,
          minimalMode,
        })
        if (countersGroup.columns.length > 0) {
          addColumnGroup(summaryColumns, countersGroup)
        }
      }
    }

    if (!hideActions) {
      addColumnGroup(summaryColumns, weekActionsColumnsGroup())
    }

    return summaryColumns
  }, [
    start,
    end,
    currentUserRole,
    displayDay,
    workPeriodValidateMutation,
    hideActions,
    minimalMode,
    setExcludeGroupKeys,
    excludeGroupKeys,
    employerRules,
    daysWithoutWorkPeriods,
    days,
    specialPeriods,
    counterTemplates,
  ])
}

export default SummaryTableV2
